/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import {getLegend} from '@ion/core/src/services/ArcGIS';
import {IonSpinner} from '../../components/loading/IonSpinner';

// defines the prop definitions for Legend Component ...
export type LegendProps = {
  layer?: __esri.Layer,
  index?: number
}

// defines the styles used by Legend Component ...
export const useStyles = makeStyles(theme => ({
  title : {
    fontFamily : 'Roboto',
    fontWeight : 'bold'
  },
  label : {
    fontFamily : 'Roboto'
  }
}));

/**
 * The Legend provides a component which allows display
 * legend based on the given layer.
 *
 * ```typescript
 *
 *    import Legend from '@ion/dls/src/editors/legend/Legend';
 *
 *    <Legend layer={layer} />
 *
 * ```
 */
export const Legend = ({layer}: LegendProps) => {
  const isMounted           = useHasMounted();
  const [legend, setLegend] = useState(null);
  const classes             = useStyles();

  useEffect(() => {
    if (isMounted) {
      loadLegend({
        url      : layer?.url,
        title    : layer?.title,
        callback : (result) => { setLegend(result); }
      });
    }
  }, [isMounted]);

  /**
  * @private
  */
  const loadLegend = async function({url, title, callback}) {
    const legendResults = await getLegend(url, title);
    isMounted && callback(legendResults);
  };

  /**
   * @private
   */
  const Legend = ({layer, index}: LegendProps) => {
    return (
      <div style={{paddingTop: index > 1 ? 8 : 0}}>
        <span className={classes.title}>{layer?.layerName}</span>
        {
          layer?.legend?.map(({imageData, label, width, height}, i) => (
            <div key={`img-${i}-${imageData}`} style={{display: 'block'}}>
              <table>
                <tbody>
                  <tr>
                    <td><img src={`data:image/jpeg;base64,${imageData}`}
                      width={width} height={height} />
                    </td>
                    {(label.length > 0) && <td><span className={classes.label}>{label}</span></td>}
                  </tr>
                </tbody>
              </table>
            </div>
          ))
        }
      </div>
    );
  };

  if (legend) {
    return (
      <div>
        {
          legend?.map((layer, index) => (
            <Legend key={`layer-${layer?.layerName}-${index}`} index={index} layer={layer} />
          ))
        }
      </div>
    );
  }

  return (
    <div style={{position: 'absolute', top: '80%', left: '50%'}}>
      <IonSpinner width='20px' height='20px' />
    </div>
  );
};

export default Legend;
